import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Send, Loader2 } from 'lucide-react';
import FormStepper from '@/components/FormStepper';
import Step1VetInfo from '@/components/Step1VetInfo';
import Step2PatientInfo from '@/components/Step2PatientInfo';
import Step3TestsObservations from '@/components/Step3TestsObservations';
import Step4ClinicalInfo from '@/components/Step4ClinicalInfo';
import Step5SampleCollection from '@/components/Step5SampleCollection';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialFormData = {
  fecha: new Date().toISOString().split('T')[0],
  hora_toma: new Date().toTimeString().slice(0, 5),
  vet_info: {
    nombre: '',
    clinica: '',
    telefono: '',
    email: '',
  },
  patient_info: {
    nombre: '',
    especie: '',
    raza: '',
    edad: '',
    sexo: '',
    propietario: '',
  },
  prueba_solicitada: '',
  observaciones: '',
  clinical_data: {
    toma_antibioticos: false,
    fecha_ultima_dosis: null,
    alergico_antibioticos: false,
    cual_antibiotico: null,
    tiempo_evolucion: null,
    origen_toma_muestra: null,
  },
  sample_collection: {
    sangre: null,
    sangre_otra: null,
    orina: null,
    orina_otra: null,
    hisopados: null,
    hisopados_otra: null,
    observaciones_conservacion: null,
  },
  cultivo_data: {
    faringea: null,
    exudado_diversos: null,
    nasal: null,
    urocultivo: null,
    hongos_tipo_lesion: null,
    hongos_sitio: null,
    coprocultivo: null,
    coprocultivo_ingesta: null,
    traqueal: null,
  },
};

export default function HomePage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.vet_info.nombre) newErrors.vet_nombre = 'Campo requerido';
      if (!formData.vet_info.clinica) newErrors.vet_clinica = 'Campo requerido';
      if (!formData.vet_info.telefono) newErrors.vet_telefono = 'Campo requerido';
      if (!formData.vet_info.email) {
        newErrors.vet_email = 'Campo requerido';
      } else if (!/\S+@\S+\.\S+/.test(formData.vet_info.email)) {
        newErrors.vet_email = 'Email inválido';
      }
    }

    if (step === 2) {
      if (!formData.patient_info.nombre) newErrors.patient_nombre = 'Campo requerido';
      if (!formData.patient_info.especie) newErrors.patient_especie = 'Campo requerido';
      if (!formData.patient_info.raza) newErrors.patient_raza = 'Campo requerido';
      if (!formData.patient_info.edad) newErrors.patient_edad = 'Campo requerido';
      if (!formData.patient_info.sexo) newErrors.patient_sexo = 'Campo requerido';
      if (!formData.patient_info.propietario) newErrors.patient_propietario = 'Campo requerido';
    }

    if (step === 3) {
      if (!formData.fecha) newErrors.fecha = 'Campo requerido';
      if (!formData.hora_toma) newErrors.hora_toma = 'Campo requerido';
      if (!formData.prueba_solicitada) newErrors.prueba_solicitada = 'Campo requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      toast.error('Por favor, complete todos los campos requeridos');
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      toast.error('Por favor, complete todos los campos requeridos');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/solicitudes`, formData);
      toast.success('¡Solicitud enviada exitosamente!');
      navigate('/confirmacion', { state: { solicitud: response.data } });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(
        error.response?.data?.detail || 'Error al enviar la solicitud. Por favor, intente nuevamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center">
            <img
              src="/amplicare-logo.svg"
              alt="Amplicare Lab"
              className="h-12"
              data-testid="amplicare-logo"
            />
          </div>
        </div>
      </header>

      {/* Stepper */}
      <FormStepper currentStep={currentStep} />

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 form-section">
          {currentStep === 1 && (
            <Step1VetInfo
              formData={formData}
              updateFormData={setFormData}
              errors={errors}
            />
          )}
          {currentStep === 2 && (
            <Step2PatientInfo
              formData={formData}
              updateFormData={setFormData}
              errors={errors}
            />
          )}
          {currentStep === 3 && (
            <Step3TestsObservations
              formData={formData}
              updateFormData={setFormData}
              errors={errors}
            />
          )}
          {currentStep === 4 && (
            <Step4ClinicalInfo
              formData={formData}
              updateFormData={setFormData}
            />
          )}
          {currentStep === 5 && (
            <Step5SampleCollection
              formData={formData}
              updateFormData={setFormData}
            />
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
              data-testid="back-button"
              className="h-12"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Anterior
            </Button>

            {currentStep < 5 ? (
              <Button
                onClick={handleNext}
                data-testid="next-button"
                className="h-12 bg-primary-amplicare hover:bg-primary-amplicare-dark text-white"
              >
                Siguiente
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                data-testid="submit-button"
                className="h-12 bg-primary-amplicare hover:bg-primary-amplicare-dark text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar Solicitud
                    <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <p className="text-center text-slate-600 text-sm">
            © 2024 Amplicare Lab - Diagnóstico Veterinario
          </p>
        </div>
      </footer>
    </div>
  );
}